import React, { useContext, useEffect } from "react";
import { Link, graphql } from "gatsby";
import styled from "styled-components";
import { PrismicRichText } from "@prismicio/react";
import { format, isValid, parseISO } from "date-fns";
import { fr, enGB } from "date-fns/locale";

// Previews
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

// SEO
import { PageSeo } from "../components/global/page-seo";

// Components
import { AspectRatioImageContainer } from "../components/containers/aspect-ratio-image-container";

// Modules
import { Newsletter } from "../components/modules/newsletter";
import { SingleArticle } from "../components/modules/single-article";

// Context
import { CurrentLanguage } from "../components/context/current-language";
import { PageColor } from "../components/context/page-color";
import { HeaderColor } from "../components/context/header-color";
import { GatsbyImage } from "gatsby-plugin-image";

const Page = styled.div`
  padding: 0 50px;

  & .remaining-articles-container {
    margin: 0 0 150px 0;

    & .single-article {
      display: block;
      margin-bottom: 100px;

      & .image-container {
        margin-bottom: 20px;
      }

      & .text-container {
        display: block;

        & .title-bar {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;

          & p {
            margin: 0;
          }

          & .spacer {
            margin: 0 10px;
          }
        }

        & .article-title {
          font-size: 24px;
          line-height: 32px;
          margin: 20px 0 10px;
        }

        & .article-excerpt {
          margin: 10px 0 20px;
        }
      }
    }
  }

  @media (max-width: 1445px) {
    padding: 0 40px;
  }

  @media (max-width: 999px) {
    padding: 0 30px;
  }

  @media (max-width: 800px) {
    padding: 0 25px;

    &.page {
      margin: 120px 0 0 0;
    }
  }

  & .remaining-articles-container {
    margin: 0 0 150px 0;

    @media (max-width: 800px) {
      & .single-article {
        grid-column: span 4;

        display: grid;
        grid-template-columns: repeat(6, 1fr);
        grid-column-gap: 25px;

        margin: 0 0 70px 0;

        &:last-of-type {
          margin: 0;
        }

        & .image-container {
          grid-column: span 3;
        }

        & .text-container {
          grid-column: span 3;
          margin: 0;

          & .article-title {
            font-size: 19px;
            line-height: 26px;

            margin: 10px 0 0 0;
          }

          & .article-excerpt {
            margin: 20px 0;
          }
        }
      }
    }

    @media (max-width: 500px) {
      & .single-article {
        & .image-container {
          grid-column: span 2;
        }

        & .text-container {
          grid-column: span 4;
        }
      }
    }
  }

  & .view-more-articles-container {
    width: 100%;

    & button {
      display: block;
      margin: 0 auto;

      border: 1px solid #09184f;

      height: 50px;
      line-height: 50px;
      width: 275px;
    }
  }
`;

const Introduction = styled.div`
  padding: 30px 30px 0 30px;

  @media (max-width: 1445px) {
    padding: 0;
  }

  &.add-margin {
    margin: 0 0 200px 0;

    @media (max-width: 800px) {
      margin: 0 0 90px 0;
    }
  }

  & .mobile-title {
    grid-column: span 6;
    display: none;

    margin: 0 0 50px 0;

    @media (max-width: 800px) {
      display: block;
    }
  }

  & .image-container {
    grid-column: 1 / 7;
    padding: 0 25px 0 0;

    @media (max-width: 1445px) {
      padding: 0;
    }

    @media (max-width: 800px) {
      grid-column: 1 / 7;
      order: 1;
    }

    & img:not([aria-hidden="true"]) {
      border: 39px solid #efefef;
      box-sizing: border-box;

      width: 100%;
      height: 100%;
      object-fit: cover;

      @media (max-width: 999px) {
        border: 27px solid #efefef;
      }
    }
  }

  & .text-container {
    grid-column: 7 / 13;
    margin: 39px 0 0 0;
    padding: 0 0 0 25px;

    @media (max-width: 1445px) {
      grid-column: 7 / 13;
      padding: 39px 0;
      margin: 0 0 0 60px;
    }

    @media (max-width: 999px) {
    }

    @media (max-width: 800px) {
      & .category-title-container {
        display: none;
      }

      grid-column: 1 / 7;
      order: 2;

      padding: 39px 25px;
      margin: 0;
    }

    & .inner-text-container {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
      height: 100%;

      max-width: 470px;

      & .category-title-container {
        width: 100%;
      }

      & .first-article-text-container {
        & .title-bar {
          display: flex;
          flex-direction: row;

          & p {
            margin: 0;
          }

          & .spacer {
            margin: 0 10px;
          }
        }

        & .text {
          & .article-title {
            margin: 20px 0;
          }

          & .article-excerpt {
            margin: 20px 0;
          }
        }
      }
    }
  }
`;

const EventCategory = ({ data }) => {
  const [currentLanguage, setCurrentLanguage] = useContext(CurrentLanguage);
  const [pageColor, setPageColor] = useContext(PageColor);
  const [headerColor, setHeaderColor] = useContext(HeaderColor);

  useEffect(() => {
    setPageColor(`#fff`);
    setHeaderColor(`#fff`);
  }, []);

  const formatEventDate = (dateBegins, dateEnds, labels, lang = "fr-fr") => {
    const locale = lang === "fr-fr" ? fr : enGB;
    const pattern = lang === "fr-fr" ? "dd-MM-yyyy" : "yyyy-MM-dd";

    const begins = parseISO(dateBegins);
    const ends = dateEnds ? parseISO(dateEnds) : null;

    if (!isValid(begins)) return null;

    const beginsFormatted = format(begins, pattern, { locale });

    if (!ends || !isValid(ends) || dateBegins === dateEnds) {
      return `${beginsFormatted}`;
    }

    const endsFormatted = format(ends, pattern, { locale });

    return `${labels?.date_from || "from"} ${beginsFormatted} ${
      labels?.date_to || "to"
    } ${endsFormatted}`;
  };

  const labelsEntries = data.prismicSiteLabels?.data?.labels || [];

  const labels = Object.fromEntries(
    labelsEntries
      .filter((item) => item.key && item.value)
      .map((item) => [item.key, item.value])
  );

  const firstArticle =
    data.allPrismicEvent.nodes.length >= 1
      ? data.allPrismicEvent.nodes.filter((event, index) => index === 0)[0]
      : null;

  const EventCard = ({ event }) => {
    const categoryTitle =
      event.data?.categories?.[0]?.category?.document?.data
        ?.title?.text || "";

    const formattedDate = formatEventDate(
      event.data.date_begins,
      event.data.date_ends,
      labels,
      event.lang
    );

    return (
      <div className="single-article">
        <div className="image-container">
          <Link to={event.url}>
            <AspectRatioImageContainer image={null} padding={140}>
              {event?.data?.featured_img?.url && (
                <img
                  src={event.data.featured_img.url}
                  alt={event.data.featured_img.alt || ""}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  loading="lazy"
                />
              )}
            </AspectRatioImageContainer>
          </Link>
        </div>

        <div className="text-container">
          <div className="title-bar tag gotham-bold uppercase">
            <p>{categoryTitle}</p>
            <p className="spacer">|</p>
            <p>{formattedDate}</p>
          </div>

          <h3 className="article-title">{event.data.title?.text}</h3>
          <p className="article-excerpt">{event.data.excerpt}</p>

          <Link to={event.url} className="uppercase small">
            {currentLanguage === `en` ? `See More` : `Voir Plus`}
          </Link>
        </div>
      </div>
    );
  };

  const remainingArticles =
    data.allPrismicEvent.nodes.length >= 1
      ? data.allPrismicEvent.nodes
          .filter((event, index) => index > 0)
          .map((event, index) => (
            <EventCard event={event} key={`event_card_${index}`} />
          ))
      : null;

  if (firstArticle !== null) {
    return (
      <>
        <PageSeo
          title={`${labels?.our_events_in || "Événements à"} ${
            firstArticle?.data?.categories?.[0]?.category?.document?.data
              ?.title?.text || ""
          }`}
          image={null}
          description={null}
          url={
            typeof window !== "undefined"
              ? window.location.href
              : `/event/category/${
                  firstArticle?.data?.categories?.[0]?.category?.uid || ""
                }`
          }
        />

        <Page className="page">
          <Introduction
            className={remainingArticles.length >= 1 ? `add-margin` : ``}
          >
            <div className="grid-12">
              <div className="mobile-title">
                <div className="category-title-container">
                  <h1 className="article-title">
                    {labels?.our_events_in || "Événements à"}{" "}
                    {firstArticle?.data?.categories?.[0]?.category?.document
                      ?.data?.title?.text || ""}
                  </h1>
                </div>
              </div>

              <div className="image-container">
                <Link to={firstArticle.url}>
                  <AspectRatioImageContainer image={null} padding={140}>
                    {firstArticle?.data?.featured_img?.url && (
                      <img
                        src={firstArticle.data.featured_img.url}
                        alt={firstArticle.data.featured_img.alt || ""}
                        fetchpriority="high"
                        loading="eager"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </AspectRatioImageContainer>
                </Link>
              </div>

              <div className="text-container">
                <div className="inner-text-container">
                  <div className="first-article-text-container">
                    <div className="text">
                      <div className="category-title-container">
                        <h1 className="article-title">
                          {labels?.our_events_in || "Événements à"}{" "}
                          {firstArticle?.data?.categories?.[0]?.category
                            ?.document?.data?.title?.text ||
                            ""}
                        </h1>
                      </div>
                      <div className="title-bar tag gotham-bold uppercase">
                        <p>
                          {firstArticle.data.categories
                            .map(
                              (item) =>
                                item.category?.document?.data
                                  ?.title?.text
                            )
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        <p className="spacer">|</p>
                        <p>
                          {formatEventDate(
                            firstArticle.data.date_begins,
                            firstArticle.data.date_ends,
                            labels,
                            firstArticle.lang
                          )}
                        </p>
                      </div>

                      {firstArticle && (
                        <>
                          <h3>{firstArticle.data.title?.text}</h3>
                          <p>{firstArticle.data?.excerpt}</p>
                        </>
                      )}

                      <p>
                        <Link to={firstArticle.url} className="uppercase small">
                          {currentLanguage === `en` ? `See More` : `Voir Plus`}
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Introduction>

          {remainingArticles && remainingArticles.length > 0 && (
            <>
              <section className="grid-4 remaining-articles-container">
                {remainingArticles}
              </section>
            </>
          )}
        </Page>
      </>
    );
  } else {
    return null;
  }
};

export default withPrismicPreview(EventCategory);

export const query = graphql`
  query EventCategoryQuery($uid: String, $lang: String) {
    allPrismicEvent(
      filter: {
        lang: { eq: $lang }
        data: { categories: { elemMatch: { category: { uid: { eq: $uid } } } } }
      }
      sort: { data: { date_begins: DESC } }
    ) {
      nodes {
        _previewable
        id
        uid
        url
        lang
        data {
          title {
            text
          }
          excerpt
          date_begins
          date_ends
          featured_img {
            url
            alt
          }
          categories {
            category {
              uid
              document {
                ... on PrismicGalleriesSingle {
                  data {
                    title {
                      text
                    }
                  }
                  uid
                  url
                  lang
                }
              }
            }
          }
        }
      }
    }

    prismicGalleriesSingle(uid: { eq: $uid }, lang: { eq: $lang }) {
      uid
      lang
      url
      data {
        title {
          text
        }
      }
    }

    prismicSiteLabels(uid: { eq: "default" }, lang: { eq: $lang }) {
      data {
        labels {
          key
          value
        }
      }
    }
  }
`;
